import { Browser, Platform, PlatformType, STORAGE_KEY } from "@/utils/enum";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore
} from "react";
import { UAParser } from "ua-parser-js";

interface DeviceInfo {
  majorVersion: string | undefined;
  browserName: string;
  isAndroid: boolean;
  isChrome: boolean;
}

interface DeviceState {
  info: DeviceInfo;
  platform: PlatformType;
}

const DEFAULT_DEVICE_STATE: DeviceState = {
  info: {
    majorVersion: "",
    browserName: "",
    isAndroid: false,
    isChrome: false
  },
  platform: Platform.Desktop
};

let browserDeviceState: DeviceState | null = null;

const getBrowserDeviceState = (): DeviceState => {
  if (typeof window === "undefined") {
    return DEFAULT_DEVICE_STATE;
  }

  if (browserDeviceState) {
    return browserDeviceState;
  }

  const parser = new UAParser();
  const result = parser.getResult();
  const os = result.os.name || "";
  const isAndroid = os.toLowerCase().includes(Platform.Android);
  const isMobile =
    !!result.device.type && result.device.type !== Platform.Desktop;

  let currentPlatform = Platform.Desktop;
  if (isMobile) {
    currentPlatform = isAndroid ? Platform.Android : Platform.Ios;
  }

  const browserInfo = result.browser;
  const name = browserInfo?.name ?? "";
  const isChrome = Boolean(name.toLowerCase().includes(Browser.Chrome));
  const majorVersion = browserInfo.major;
  const browserName = isChrome ? Browser.Chrome : Browser.Safari;

  browserDeviceState = {
    info: {
      isAndroid,
      isChrome,
      browserName,
      majorVersion
    },
    platform: currentPlatform
  };

  return browserDeviceState;
};

const subscribeDeviceState = () => () => undefined;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PWAContextType {
  deviceInfo: DeviceInfo;
  isInstallable: boolean;
  isInstalled: boolean;
  platform: PlatformType;
  deferredPrompt: BeforeInstallPromptEvent | null;
  installPWA: (setOpenModal?: (value: boolean) => void) => void;
  isHideBanner: boolean;
  handleHiddenBanner: (value: boolean) => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: ReactNode;
}

export const PWAProvider = ({ children }: PWAProviderProps) => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { info, platform } = useSyncExternalStore(
    subscribeDeviceState,
    getBrowserDeviceState,
    () => DEFAULT_DEVICE_STATE
  );

  const [isHideBanner, setHideBanner] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    return localStorage.getItem(STORAGE_KEY.BANNER_INSTALL_PWA) === "true";
  });

  useEffect(() => {
    const checkIfInstalled = () => {
      if (
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true
      ) {
        setIsInstalled(true);
        setHideBanner(true);
        return true;
      }
      return false;
    };

    if (checkIfInstalled()) {
      return;
    }

    // Handle beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
    };

    // Handle appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      setHideBanner(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleHiddenBanner = useCallback((value: boolean) => {
    setHideBanner(value);
    localStorage.setItem(STORAGE_KEY.BANNER_INSTALL_PWA, `${value}`);
  }, []);

  const installPWA = useCallback(
    (setOpenModal?: (value: boolean) => void) => {
      if (platform === Platform.Android || platform === Platform.Desktop) {
        if (deferredPrompt) {
          deferredPrompt.prompt();
          deferredPrompt.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === "accepted") {
              setDeferredPrompt(null);
              setIsInstallable(false);
            }

            setOpenModal?.(false);
          });
        } else {
          alert(
            "インストールの準備ができていません。しばらくしてからもう一度お試しください。" // Not ready to install. Please try again later.
          );
        }
      } else if (platform === Platform.Ios) {
        // iOS requires manual installation via Safari share menu
        // Show instructions to user
      } else if (platform === Platform.DesktopSafari) {
        alert("SafariのデスクトップではPWAインストールは未対応です"); // PWA installation is not supported on Safari desktop.
        setOpenModal?.(false);
      }
    },
    [platform, deferredPrompt]
  );

  const value: PWAContextType = {
    deviceInfo: info,
    isInstallable,
    isInstalled,
    platform,
    deferredPrompt,
    installPWA,
    isHideBanner,
    handleHiddenBanner
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};

export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
};
